import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/customSupabaseClient';
import { toast } from 'sonner';

export const useExerciseBookmarks = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchBookmarks = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [bookmarksRes, collectionsRes] = await Promise.all([
        supabase
          .from('user_exercise_bookmarks')
          .select('id, exercise_id, collection_id, notes, sort_order, created_at, exercises(id, name, difficulty, primary_region, video_thumbnail_url)')
          .eq('user_id', user.id)
          .order('sort_order', { ascending: true, nullsFirst: false })
          .order('created_at', { ascending: false }),
        supabase
          .from('exercise_bookmark_collections')
          .select('*')
          .eq('user_id', user.id)
          .order('name', { ascending: true }),
      ]);

      if (bookmarksRes.error) throw bookmarksRes.error;
      if (collectionsRes.error) throw collectionsRes.error;

      setBookmarks(bookmarksRes.data || []);
      setCollections(collectionsRes.data || []);
    } catch (err) {
      toast.error('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBookmarks(); }, [fetchBookmarks]);

  const isBookmarked = useCallback((exerciseId) => {
    return bookmarks.some(b => b.exercise_id === exerciseId);
  }, [bookmarks]);

  const getBookmark = useCallback((exerciseId) => {
    return bookmarks.find(b => b.exercise_id === exerciseId);
  }, [bookmarks]);

  const toggleBookmark = useCallback(async (exerciseId, collectionId = null) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { toast.error('You must be logged in'); return; }

    const existing = bookmarks.find(b => b.exercise_id === exerciseId);
    if (existing) {
      const { error } = await supabase
        .from('user_exercise_bookmarks')
        .delete()
        .eq('id', existing.id);
      if (error) { toast.error('Failed to remove bookmark'); return; }
      setBookmarks(prev => prev.filter(b => b.id !== existing.id));
      toast.success('Bookmark removed');
    } else {
      const { data, error } = await supabase
        .from('user_exercise_bookmarks')
        .insert({ user_id: user.id, exercise_id: exerciseId, collection_id: collectionId, sort_order: 0 })
        .select('id, exercise_id, collection_id, notes, sort_order, created_at, exercises(id, name, difficulty, primary_region, video_thumbnail_url)')
        .single();
      if (error) { toast.error('Failed to add bookmark'); return; }
      setBookmarks(prev => [data, ...prev]);
      toast.success('Exercise bookmarked');
    }
  }, [bookmarks]);

  const moveToCollection = useCallback(async (bookmarkId, collectionId) => {
    const { error } = await supabase
      .from('user_exercise_bookmarks')
      .update({ collection_id: collectionId })
      .eq('id', bookmarkId);
    if (error) { toast.error('Failed to move bookmark'); return; }
    setBookmarks(prev => prev.map(b => b.id === bookmarkId ? { ...b, collection_id: collectionId } : b));
  }, []);

  const updateBookmarkNotes = useCallback(async (bookmarkId, notes) => {
    const { error } = await supabase
      .from('user_exercise_bookmarks')
      .update({ notes })
      .eq('id', bookmarkId);
    if (error) { toast.error('Failed to update notes'); return; }
    setBookmarks(prev => prev.map(b => b.id === bookmarkId ? { ...b, notes } : b));
  }, []);

  const createCollection = useCallback(async (name, description = '', color = 'default') => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('exercise_bookmark_collections')
      .insert({ user_id: user.id, name, description, color })
      .select()
      .single();
    if (error) {
      toast.error(error.message.includes('duplicate') ? 'Collection name already exists' : 'Failed to create collection');
      return null;
    }
    setCollections(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    toast.success(`Collection "${name}" created`);
    return data;
  }, []);

  const deleteCollection = useCallback(async (collectionId) => {
    const { error } = await supabase
      .from('exercise_bookmark_collections')
      .delete()
      .eq('id', collectionId);
    if (error) { toast.error('Failed to delete collection'); return; }
    setCollections(prev => prev.filter(c => c.id !== collectionId));
    setBookmarks(prev => prev.map(b => b.collection_id === collectionId ? { ...b, collection_id: null } : b));
    toast.success('Collection deleted');
  }, []);

  const reorderBookmarks = useCallback(async (orderedIds) => {
    setBookmarks(prev => {
      const map = new Map(prev.map(b => [b.id, b]));
      const reordered = orderedIds.map((id, i) => ({ ...map.get(id), sort_order: i })).filter(Boolean);
      const remaining = prev.filter(b => !orderedIds.includes(b.id));
      return [...reordered, ...remaining];
    });

    const updates = orderedIds.map((id, index) =>
      supabase.from('user_exercise_bookmarks').update({ sort_order: index }).eq('id', id)
    );
    const results = await Promise.all(updates);
    const failed = results.some(r => r.error);
    if (failed) toast.error('Failed to save new order');
  }, []);

  const removeBookmarks = useCallback(async (bookmarkIds) => {
    if (!bookmarkIds.length) return;
    const { error } = await supabase
      .from('user_exercise_bookmarks')
      .delete()
      .in('id', bookmarkIds);
    if (error) { toast.error('Failed to remove bookmarks'); return false; }
    setBookmarks(prev => prev.filter(b => !bookmarkIds.includes(b.id)));
    toast.success(`${bookmarkIds.length} bookmark${bookmarkIds.length > 1 ? 's' : ''} removed`);
    return true;
  }, []);

  const renameCollection = useCallback(async (collectionId, name) => {
    const { error } = await supabase
      .from('exercise_bookmark_collections')
      .update({ name, updated_at: new Date().toISOString() })
      .eq('id', collectionId);
    if (error) { toast.error('Failed to rename collection'); return; }
    setCollections(prev => prev.map(c => c.id === collectionId ? { ...c, name } : c));
  }, []);

  return {
    bookmarks,
    collections,
    loading,
    isBookmarked,
    getBookmark,
    toggleBookmark,
    removeBookmarks,
    reorderBookmarks,
    moveToCollection,
    updateBookmarkNotes,
    createCollection,
    deleteCollection,
    renameCollection,
    refresh: fetchBookmarks,
  };
};
